import { normalize as rawNormalize } from '@spazious/jsonapi-tools';

/**
 * Minimal JSON:API envelope shape. Accepts any response produced by the
 * `@spazious/client-backoffice` generated client whose types structurally
 * disagree with `JSONApi.Response<T>` from `@spazious/jsonapi-tools`
 * (notably `links.self`, typed as an object union upstream vs a string here).
 *
 * The `JSONApi` namespace is declared but not exported from `@spazious/jsonapi-tools`,
 * so we cannot consume it directly. This wrapper contains the structural cast in
 * a single place; remove it once the two packages' types are reconciled.
 */
export type JsonApiResponseLike = {
  data?: unknown;
  meta?: unknown;
  included?: unknown;
  links?: unknown;
};

export type NormalizedResource = Record<string, unknown>;

/**
 * Normalizes a JSON:API envelope into the flattened resource shape.
 *
 * Pass a domain type argument to have the hook own the normalized shape and
 * return typed data (`normalizeApiResponse<MeetingRoomRate>(data)`); consumers
 * then import that type from the hook instead of re-declaring/casting it. The
 * `as` is the documented boundary exception — `@spazious/jsonapi-tools` types
 * the result as `Record<string, any>`, so the domain shape is asserted here,
 * in the single place the cast is allowed.
 */
export const normalizeApiResponse = <T = NormalizedResource>(
  response: JsonApiResponseLike
): T | T[] => rawNormalize(response as never) as T | T[];

/**
 * `normalizeApiResponse` for list envelopes: a JSON:API list payload always
 * normalizes to an array, so this collapses the `T | T[]` union for callers
 * of list endpoints. Same boundary-cast rules as above.
 */
export const normalizeApiResponseList = <T = NormalizedResource>(
  response: JsonApiResponseLike
): T[] => rawNormalize(response as never) as T[];
